import { CSSProperties, ReactNode, useEffect, useRef, useState } from 'react';
import { Modal } from 'reactstrap';
import classNames from 'classnames';

import useIsMobile from '../hooks/useIsMobile';
import { acquireScrollLock, releaseScrollLock } from '../utils/scrollLock';

import styles from './BaseModal.module.scss';

const DEFAULT_MODAL_WIDTH = 466;
const MODAL_HORIZONTAL_OFFSET = 8;
const MODAL_Z_INDEX = 2100;

export type BaseModalProps = {
  isOpen: boolean;
  unmountOnClose?: boolean;
  children?: ReactNode;
  className?: string;
  customStyle?: CSSProperties;
  isDiscover?: boolean;
  noPadding?: boolean;
  toggle?: () => void;
  width?: number;
  contentClassName?: string;
};

const BaseModal = (props: BaseModalProps): JSX.Element => {
  const {
    customStyle,
    isOpen,
    unmountOnClose = true,
    children,
    className,
    toggle,
    isDiscover,
    noPadding,
    width = DEFAULT_MODAL_WIDTH,
    contentClassName,
  } = props;

  const { isMobile } = useIsMobile();
  const [closedInternally, setClosedInternally] = useState(false);
  const toggleRef = useRef(toggle);

  useEffect(() => {
    toggleRef.current = toggle;
  }, [toggle]);

  useEffect(() => {
    if (isOpen) setClosedInternally(false);
  }, [isOpen]);

  const effectiveIsOpen = isOpen && !closedInternally;

  const handleToggle = () => {
    setClosedInternally(true);
  };

  const handleClosed = () => {
    toggleRef.current?.();
  };

  useEffect(() => {
    if (!effectiveIsOpen) return undefined;

    acquireScrollLock();

    return () => releaseScrollLock();
  }, [effectiveIsOpen]);

  const modalWidthBasicStyles: CSSProperties = {
    maxWidth: width,
    margin: 'auto',
    position: 'absolute',
    left: MODAL_HORIZONTAL_OFFSET,
    right: MODAL_HORIZONTAL_OFFSET,
  };

  return (
    <Modal
      centered
      isOpen={effectiveIsOpen}
      modalClassName={className}
      backdropClassName={styles.backdrop}
      contentClassName={classNames(
        isDiscover ? styles.modalContentDiscover : styles.modalContent,
        contentClassName,
        {
          [styles.mobile]: isMobile,
          [styles.web]: !isMobile,
          [styles.noPadding]: noPadding,
        },
      )}
      zIndex={MODAL_Z_INDEX}
      style={customStyle || modalWidthBasicStyles}
      unmountOnClose={unmountOnClose}
      toggle={handleToggle}
      onClosed={handleClosed}
    >
      {children}
    </Modal>
  );
};

export default BaseModal;
